import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  DocumentTextIcon,
  UserGroupIcon,
  AcademicCapIcon,
  ArrowDownTrayIcon,
  CheckIcon,
  ArrowRightIcon,
  ChartBarIcon,
  LightBulbIcon,
  RocketLaunchIcon,
} from '@heroicons/react/24/outline';

const ServicesPage: React.FC = () => {
  const services = [
    {
      name: 'Custom Reports',
      price: '£500-2000',
      timeframe: 'per report',
      description: 'Tailored trend analysis reports for your specific needs',
      icon: DocumentTextIcon,
      resultIcon: ChartBarIcon,
      primaryResult: 'Strategic insights that drive business decisions',
      secondaryResults: [
        'Identify untapped market opportunities',
        'Understand competitive landscape',
        'Make data-driven strategic choices',
        'Present compelling insights to stakeholders',
      ],
      features: [
        'Industry-specific trend analysis',
        'Competitive landscape insights',
        'Custom data visualisations',
        'Executive summary and recommendations',
        'White-label options available',
        'Delivery within 5-10 business days',
      ],
      bestFor: 'Businesses needing detailed trend analysis for strategic planning',
      cta: 'Request Custom Report',
    },
    {
      name: 'Consulting',
      price: '£150-300',
      timeframe: 'per hour',
      description: 'Expert trend strategy consulting and guidance',
      icon: UserGroupIcon,
      resultIcon: LightBulbIcon,
      primaryResult: 'Expert guidance to maximise trend opportunities',
      secondaryResults: [
        'Develop winning trend strategies',
        'Optimise product development roadmap',
        'Enhance brand positioning',
        'Accelerate market entry success',
      ],
      features: [
        'One-to-one strategy sessions',
        'Trend opportunity identification',
        'Market entry recommendations',
        'Product development insights',
        'Brand positioning guidance',
        'Ongoing advisory support',
      ],
      bestFor: 'Companies looking for expert guidance on trend strategy',
      cta: 'Book Consultation',
    },
    {
      name: 'Training',
      price: '£2000-5000',
      timeframe: 'per workshop',
      description: 'Team workshops and training sessions',
      icon: AcademicCapIcon,
      resultIcon: RocketLaunchIcon,
      primaryResult: 'Build internal trend analysis capabilities',
      secondaryResults: [
        'Empower your team with trend skills',
        'Reduce dependency on external consultants',
        'Foster data-driven decision making',
        'Create competitive advantage through insights',
      ],
      features: [
        'Customised training programmes',
        'Hands-on trend analysis workshops',
        'Tool and platform training',
        'Best practices and methodologies',
        'Team certification programmes',
        'Follow-up support and resources',
      ],
      bestFor: 'Organisations wanting to build internal trend analysis capabilities',
      cta: 'Schedule Training',
    },
    {
      name: 'Data Exports',
      price: '£100-500',
      timeframe: 'per export',
      description: 'Historical trend data exports',
      icon: ArrowDownTrayIcon,
      resultIcon: ChartBarIcon,
      primaryResult: 'Comprehensive historical data for analysis',
      secondaryResults: [
        'Access up to 2 years of trend data',
        'Multiple export formats available',
        'Custom date ranges and filters',
        'Validated and quality-checked data',
      ],
      features: [
        'Historical trend data (up to 2 years)',
        'Multiple export formats (CSV, JSON, Excel)',
        'Custom date ranges',
        'Filtered data sets',
        'Data validation and quality checks',
        'Delivery within 24-48 hours',
      ],
      bestFor: 'Researchers and analysts needing historical trend data',
      cta: 'Request Data Export',
    },
  ];



  return (
    <div className="bg-white">
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-purple-50 to-indigo-100">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-4xl md:text-5xl font-bold text-gray-900 mb-6"
          >
            Professional Services
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-xl text-gray-600 max-w-2xl mx-auto mb-8"
          >
            Beyond our subscription plans, we offer specialised services to help you maximise the value of trend data for your business.
          </motion.p>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {services.map((service, index) => (
              <motion.div
                key={service.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-white rounded-xl shadow-lg border border-gray-200 p-8 hover:shadow-xl transition-shadow"
              >
                {/* Header */}
                <div className="flex items-start justify-between mb-6">
                  <div className="flex items-center">
                    <div className="inline-flex items-center justify-center w-12 h-12 bg-purple-100 rounded-lg mr-4">
                      <service.icon className="h-6 w-6 text-purple-600" />
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold text-gray-900">{service.name}</h3>
                      <p className="text-gray-600">{service.description}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-bold text-purple-600">{service.price}</div>
                    <div className="text-sm text-gray-500">{service.timeframe}</div>
                  </div>
                </div>

                {/* Results Focus */}
                <div className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-lg p-6 mb-6">
                  <div className="flex items-center mb-4">
                    <service.resultIcon className="h-6 w-6 text-purple-600 mr-2" />
                    <h4 className="text-lg font-semibold text-gray-900">What you'll achieve</h4>
                  </div>
                  <p className="text-lg font-medium text-gray-900 mb-3">{service.primaryResult}</p>
                  <ul className="space-y-2">
                    {service.secondaryResults.map((result, idx) => (
                      <li key={idx} className="flex items-start">
                        <CheckIcon className="h-4 w-4 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                        <span className="text-sm text-gray-700">{result}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Features */}
                <div className="mb-6">
                  <h5 className="text-sm font-semibold text-gray-900 mb-3 uppercase tracking-wide">What's included</h5>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {service.features.map((feature) => (
                      <div key={feature} className="flex items-start">
                        <CheckIcon className="h-4 w-4 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                        <span className="text-sm text-gray-700">{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Best For */}
                <div className="bg-gray-50 rounded-lg p-4 mb-6">
                  <p className="text-sm text-gray-600 italic">"{service.bestFor}"</p>
                </div>

                {/* CTA */}
                <Link
                  to={`/contact?type=${service.name.toLowerCase().replace(/\s+/g, '-')}`}
                  className="w-full bg-purple-600 text-white py-3 px-6 rounded-lg font-medium hover:bg-purple-700 transition-colors inline-flex items-center justify-center"
                >
                  {service.cta}
                  <ArrowRightIcon className="ml-2 h-5 w-5" />
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Our Services */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Why Choose Our Services?
            </h2>
            <p className="text-xl text-gray-600">
              Expert-led solutions backed by cutting-edge trend analysis technology
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="bg-white rounded-lg p-6 shadow-sm border border-gray-200 text-center"
            >
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                <svg className="h-6 w-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Expert Team</h3>
              <p className="text-gray-600">
                Our specialists have years of experience in trend analysis and business strategy across multiple industries.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="bg-white rounded-lg p-6 shadow-sm border border-gray-200 text-center"
            >
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                <svg className="h-6 w-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Fast Delivery</h3>
              <p className="text-gray-600">
                Quick turnaround times with most services delivered within 24-48 hours, custom reports within 5-10 business days.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="bg-white rounded-lg p-6 shadow-sm border border-gray-200 text-center"
            >
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                <svg className="h-6 w-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Tailored Solutions</h3>
              <p className="text-gray-600">
                Every service is customised to your specific needs, industry, and business objectives.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Ready to Get Started?
            </h2>
            <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
              Contact our team to discuss your specific needs and get a personalised quote for our professional services.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact?type=general"
                className="bg-purple-600 text-white px-8 py-3 rounded-lg font-medium hover:bg-purple-700 transition-colors inline-flex items-center justify-center"
              >
                Contact Our Team
                <ArrowRightIcon className="ml-2 h-5 w-5" />
              </Link>
              <Link
                to="/pricing"
                className="border border-gray-300 text-gray-700 px-8 py-3 rounded-lg font-medium hover:bg-gray-50 transition-colors"
              >
                View Pricing Plans
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage; 